/**
 * ChatSessionListSkeleton
 *
 * Initial-mount loading state for the sidebar chat list. Renders shimmer-free
 * skeleton rows that mirror the dimensions of real ChatThreadItem entries so
 * there's no layout shift when real data arrives.
 *
 * Matches the app's flat design system — no shadows, no gradients, just subtle
 * muted surfaces with a gentle pulse.
 */

import React from 'react';

import { cn } from '../utils/cn';

/** Pseudo-random but stable widths so each mount renders the same shape. */
const TITLE_WIDTHS = ['w-4/5', 'w-2/3', 'w-3/4', 'w-5/6', 'w-1/2', 'w-3/5'];
const META_WIDTHS = ['w-10', 'w-12', 'w-8', 'w-14', 'w-9', 'w-11'];

export const ChatSessionListSkeleton: React.FC<{ rows?: number }> = ({ rows = 6 }) => {
  return (
    <div
      className="flex flex-col animate-in fade-in duration-300"
      role="status"
      aria-label="Loading chats"
      aria-live="polite"
    >
      {/* Faux group header — mimics "Today"-style separator */}
      <div className="px-4 py-1.5 flex items-center justify-between">
        <div className="h-2.5 w-14 rounded-sm bg-muted/40 animate-pulse" />
        <div className="h-[18px] w-6 rounded-full bg-muted/40 animate-pulse" />
      </div>

      {/* Faux rows */}
      <div className="px-2 py-2 space-y-0.5">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="px-3 py-2.5 rounded-lg"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {/* Title line */}
            <div
              className={cn(
                'h-3 rounded-sm bg-muted/40 animate-pulse mb-2',
                TITLE_WIDTHS[i % TITLE_WIDTHS.length]
              )}
              style={{ animationDelay: `${i * 60}ms` }}
            />
            {/* Meta line (timestamp + count) */}
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'h-2 rounded-sm bg-muted/30 animate-pulse',
                  META_WIDTHS[i % META_WIDTHS.length]
                )}
                style={{ animationDelay: `${i * 60 + 100}ms` }}
              />
              <div
                className="h-2 w-6 rounded-sm bg-muted/30 animate-pulse"
                style={{ animationDelay: `${i * 60 + 150}ms` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Accessible text — visually hidden */}
      <span className="sr-only">Loading your conversations…</span>
    </div>
  );
};
